import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Chip, Autocomplete, TextField } from '@mui/material';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/misc';
import { useCreateRecruiterNote, useUpdateRecruiterNote } from '@/hooks/useCandidate';
import { Spinner } from '@/components/common';

interface RecruiterNoteModalProps {
  open: boolean;
  onClose: () => void;
  candidateProfileId: string;
  candidateName: string;
  existingNote?: { id: string; notes: string; tags: string[] } | null;
}

const COMMON_TAGS = ['promising', 'needs-interview', 'senior-level', 'hired', 'rejected', 'keep-in-touch', 'onboarding-july'];

export function RecruiterNoteModal({ open, onClose, candidateProfileId, candidateName, existingNote }: RecruiterNoteModalProps) {
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  
  const { mutate: createNote, isPending: creating } = useCreateRecruiterNote();
  const { mutate: updateNote, isPending: updating } = useUpdateRecruiterNote();

  useEffect(() => {
    if (open) {
      if (existingNote) {
        setNotes(existingNote.notes || '');
        setTags(existingNote.tags || []);
      } else {
        setNotes('');
        setTags([]);
      }
    }
  }, [open, existingNote]);

  const handleSave = () => {
    if (existingNote?.id) {
      updateNote({ id: existingNote.id, data: { notes, tags } }, {
        onSuccess: () => {
          onClose();
        }
      });
    } else {
      createNote({ candidateProfileId, notes, tags }, {
        onSuccess: () => {
          onClose();
        }
      });
    }
  };

  const isPending = creating || updating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { borderRadius: '24px', p: 1 } }}>
      <DialogTitle className="font-display font-bold text-xl pb-2">
        Ghi chú ứng viên
      </DialogTitle>
      <DialogContent>
        <p className="text-sm text-gray-500 mb-5">
          Ghi chú dành riêng cho nhà tuyển dụng đối với ứng viên <strong className="text-gray-800">{candidateName}</strong>. Ứng viên sẽ không thấy được thông tin này.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Nội dung ghi chú</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ví dụ: Ứng viên tiềm năng, cần phỏng vấn thêm về kỹ năng quản lý..."
              rows={4}
              className="resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Nhãn (Tags)</label>
            <Autocomplete
              multiple
              freeSolo
              options={COMMON_TAGS}
              value={tags}
              onChange={(_, newValue) => setTags(newValue as string[])}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Nhập hoặc chọn tag..."
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused fieldset': {
                        borderColor: '#f97316',
                        borderWidth: '1px'
                      }
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button variant="outline" onClick={onClose} disabled={isPending} className="rounded-xl h-10 px-5">
          Hủy
        </Button>
        <Button onClick={handleSave} disabled={isPending} className="rounded-xl h-10 px-5 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold shadow-md">
          {isPending ? <Spinner size="sm" /> : 'Lưu ghi chú'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
